import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Grid,
  TextField,
  Button,
  MenuItem,
  Autocomplete,
  Typography,
  Paper,
  Box,
  InputLabel,
  Select,
  FormControl,
  Card,
  Avatar,
  IconButton,
  FormHelperText,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { FirstPage, ChevronLeft, ChevronRight, LastPage, Edit } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { useQuery } from "@tanstack/react-query";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import handleExport from "utils/exportData";
import { IconTableExport } from "@tabler/icons-react";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import MDAutocomplete from "components/MDAutocomplete";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDTextField from "components/MDTextField";
import DownloadIcon from "@mui/icons-material/Download";
import { convertUtcToIST } from "utils/ISTConvert";
import CloseIcon from "@mui/icons-material/Close";

const UserList = () => {
  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [filters, setFilters] = useState({
    txtUserName: "",
    txtEmail: "",
    intPhone: "",
    txtDistrict: "",
    // txtGender: "",
    intStatus: "",
    // txtDateofJoin: null,
  });
  // const genders = [
  //   { title: "Male", value: "Male" },
  //   { title: "Female", value: "Female" },
  //   { title: "Other", value: "Other" },
  // ];
  const [sorting, setSorting] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [exportData, setExportData] = useState([]);
  const [message, setMessage] = useState({
    status: "",
    title: "",
    msg: "",
  });
  const [openImage, setOpenImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const navigate = useNavigate();

  const fetchUsers = async (
    pageIndex = 0,
    pageSize = 10,
    filterValues = filters,
    sortValues = sorting,
    columnFilters = []
  ) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pageIndex + 1,
        limit: pageSize,
        columnFilters: JSON.stringify(columnFilters),
        txtUserName: filterValues.txtUserName,
        txtEmail: filterValues.txtEmail,
        txtDistrict: filterValues.txtDistrict,
        intPhone: filterValues.intPhone,
        // txtGender: filterValues.txtGender,
        intStatus: filterValues.intStatus,
        // txtDateofJoin: filterValues.txtDateofJoin
        //   ? dayjs(filterValues.txtDateofJoin).format("DD-MM-YYYY")
        //   : "",
      });

      // Append sorting info
      if (sortValues.length > 0) {
        const sortField = sortValues[0].id;
        const sortOrder = sortValues[0].desc ? "desc" : "asc";
        queryParams.append("sortField", sortField);
        queryParams.append("sortOrder", sortOrder);
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/user/all?${queryParams.toString()}`,
        {
          withCredentials: true,
        }
      );

      if (res.data.status === 200) {
        setData(res.data.data);
        setRowCount(res.data.total ?? 0);
        setExportData(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers(pagination.pageIndex, pagination.pageSize, filters, sorting, columnFilters);
  }, [pagination, sorting, columnFilters]);

  const handleFirstPage = () => {
    if (pagination.page > 0) fetchUsers(0);
  };

  const handlePrevPage = () => {
    if (pagination.page > 0) fetchUsers(pagination.page - 1);
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages - 1) fetchUsers(pagination.page + 1);
  };

  const handleLastPage = () => {
    if (pagination.page < pagination.totalPages - 1) fetchUsers(pagination.totalPages - 1);
  };

  const fetchDistrict = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/districts/all`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: districtData,
    error: districtError,
    isLoading: districtIsLoading,
  } = useQuery({
    queryKey: ["fetchDistrict"],
    queryFn: fetchDistrict,
    retry: false,
  });

  const fetchStatus = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/status/all`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: fetchedStatus,
    error: statusError,
    isLoading: statusIsLoading,
  } = useQuery({
    queryKey: ["fetchStatus"],
    queryFn: fetchStatus,
    retry: false,
  });

  const fetchAllUsers = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/user/all?page=1&limit=10000`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: usersData,
    error: usersError,
    isLoading: usersIsLoading,
  } = useQuery({
    queryKey: ["fetchAllUsers"],
    queryFn: fetchAllUsers,
    retry: false,
  });

  const allUsersData = useMemo(() => {
    if (!usersData) return [];
    const uniqueNames = [...new Set(usersData.map((user) => user.user_name).filter(Boolean))];
    return uniqueNames.map((name) => ({
      label: name,
      value: name,
    }));
  }, [usersData]);

  const statusData =
    fetchedStatus?.map((status) => ({
      label: status.name,
      value: status.status_id,
    })) || [];

  const districtOptions =
    districtData?.map((district) => ({
      label: district.title,
      value: district.value,
    })) || [];

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    // console.log("🚀 ~ fetchUsers ~ response:", response);
    return response.data;
  };

  const {
    data: meData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["me"], // Unique key for the query
    queryFn: me, // The async function to run
    retry: false,
  });

  const columns = useMemo(
    () => [
      // const columns = [
      {
        accessorKey: "profile_image",
        header: "Profile",
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const profileImage = row.original.profile_image;
          const userName = row.original.user_name || "";

          return (
            <Avatar
              src={profileImage || ""}
              alt={userName}
              sx={{
                width: 80,
                height: 80,
                cursor: profileImage ? "pointer" : "default",
                bgcolor: profileImage ? "transparent" : "#9696965c",
                fontSize: 32,
              }}
              onClick={() => {
                if (profileImage) {
                  setSelectedImage(profileImage);
                  setOpenImage(true);
                }
              }}
            >
              {!profileImage && userName.charAt(0).toUpperCase()}
            </Avatar>
          );
        },
        enableColumnActions: false,
        enableSorting: false,
        size: 150,
      },
      {
        accessorKey: "user_name",
        header: "Name",
        filterVariant: "autocomplete",
        filterSelectOptions: allUsersData,
        Cell: ({ row }) => row.original.user_name ?? "-",
        // size: 150,
      },
      {
        accessorKey: "district",
        header: "District",
        // eslint-disable-next-line react/prop-types
        Filter: ({ column }) => {
          return (
            <Autocomplete
              options={districtOptions}
              value={
                // eslint-disable-next-line react/prop-types
                districtOptions.find((option) => option.label === column.getFilterValue()) || null
              }
              onChange={(event, newValue) => {
                // eslint-disable-next-line react/prop-types
                column.setFilterValue(newValue ? newValue.label : undefined);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Filter by District"
                  variant="standard"
                  sx={{
                    "& .MuiInputBase-root": {
                      color: "#fff",
                      fontSize: "12px", // Match header font size
                    },
                    "& .MuiInput-underline:before": {
                      borderBottomColor: "#e0e0e0",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#fff",
                      transform: "scaleX(1)",
                    },
                    "& .MuiSvgIcon-root": {
                      // Dropdown arrow
                      color: "#fff",
                    },
                  }}
                />
              )}
              sx={{ minWidth: "120px" }}
            />
          );
        },
        Cell: ({ row }) => {
          const districtId = row.original.district;
          const district = districtData?.find((d) => d.value === districtId);
          return district?.title ?? "-";
        },
        // size: 120,
      },
      {
        accessorKey: "email",
        header: "Email Id",
        // size: 300,
        Cell: ({ row }) => {
          const email = row.original.email ?? "-";
          const isLong = email.length > 20;
          const displayEmail = isLong ? email.slice(0, 20) + "..." : email;

          return isLong ? (
            <Tooltip title={email} arrow>
              <span>{displayEmail}</span>
            </Tooltip>
          ) : (
            <span>{displayEmail}</span>
          );
        },
      },
      {
        accessorKey: "phone",
        header: "Mobile No",
        Cell: ({ row }) => row.original.phone ?? "-",
        // size: 130,
      },
      {
        accessorKey: "status",
        header: "Status",
        // eslint-disable-next-line react/prop-types
        Filter: ({ column }) => {
          return (
            <Autocomplete
              options={statusData}
              value={
                // eslint-disable-next-line react/prop-types
                statusData.find((option) => option.label === column.getFilterValue()) || null
              }
              onChange={(event, newValue) => {
                // eslint-disable-next-line react/prop-types
                column.setFilterValue(newValue ? newValue.label : undefined);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Status"
                  variant="standard"
                  sx={{
                    "& .MuiInputBase-root": {
                      color: "#fff",
                      fontSize: "12px",
                    },
                    "& .MuiInput-underline:before": {
                      borderBottomColor: "#e0e0e0",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#fff",
                      transform: "scaleX(1)",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#fff",
                    },
                  }}
                />
              )}
              sx={{ minWidth: "100px" }}
            />
          );
        },
        Cell: ({ row }) => {
          const statusId = row.original.status;
          const status = statusData?.find((s) => s.value === statusId);
          return (
            <Chip
              label={status?.label ?? "-"}
              sx={{
                height: "auto",
                "& .MuiChip-label": {
                  color: "white !important",
                  fontSize: "14px",
                  fontWeight: "bold",
                },
              }}
              color={
                status?.label === "Active"
                  ? "success"
                  : status?.label === "Inactive"
                    ? "error"
                    : "warning"
              }
            />
          );
        },
        // size: 120,
      },
      {
        accessorKey: "edit",
        header: "Action",
        enableColumnFilter: false,
        // size: 100,
        Cell: ({ row }) => {
          return (
            // Only show Edit button if row is selected
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title={"Click to edit"}>
                <span>
                  {" "}
                  {/* Needed to wrap disabled buttons with tooltips */}
                  <IconButton
                    key={row.user_id}
                    // onClick={() => {
                    //   navigate(`/user-edit/${row.original.user_id}`);
                    // }}
                    onClick={() => handleEditClick(row.original.user_id)}
                    disabled={loading}
                    sx={{
                      color: "#f48c06",
                      backgroundColor: "#fff",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                      transition: "background-color 0.3s, transform 0.3s",
                      "&:hover": {
                        backgroundColor: "#f48c06",
                        color: "#fff",
                        transform: "scale(1.05)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Edit sx={{ width: 20, height: 20 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ); // Hide button if not selected
        },
        enableSorting: false,
      },

      // return columns;
    ],
    [allUsersData, districtOptions, statusData]
  );

  const handleExportData = async () => {
    try {
      let formattedData = [];

      if (rowSelection && Object.keys(rowSelection).length > 0) {
        formattedData = exportData.filter((item) => rowSelection[item.id]);
      } else {
        const queryParams = new URLSearchParams({
          txtUserName: filters.txtUserName,
          txtEmail: filters.txtEmail,
          txtDistrict: filters.txtDistrict,
          intPhone: filters.intPhone,
          // txtGender: filters.txtGender,
          intStatus: filters.intStatus,
          // txtDateofJoin: filters.txtDateofJoin
          //   ? dayjs(filters.txtDateofJoin).format("DD-MM-YYYY")
          //   : "",
          page: 1,
          limit: 10000,
        });

        if (sorting.length > 0) {
          const sortField = sorting[0].id;
          const sortOrder = sorting[0].desc ? "desc" : "asc";
          queryParams.append("sortField", sortField);
          queryParams.append("sortOrder", sortOrder);
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/user/all?${queryParams.toString()}`,
          {
            withCredentials: true,
          }
        );

        if (res.data.status === 200) {
          formattedData = res.data.data;
        }
      }

      if (formattedData.length === 0) {
        console.log("No data found to export");
        setMessage({
          status: "error",
          title: "Error",
          msg: "No data found to export.",
        });
        return;
      }

      await handleExport(formattedData, "User_List_Data", districtData, statusData);

      setMessage({
        status: "success",
        title: "Success",
        msg: "Excel downloaded successfully!",
      });
    } catch (error) {
      console.error(" Export error:", error);
      setMessage({
        status: "error",
        title: "Error",
        msg: "Something went wrong during export.",
      });
    }
  };

  const handleEditClick = (userId) => {
    setSelectedUserId(userId);
    setShowConfirmDialog(true);
  };

  const handleConfirmEdit = () => {
    setShowConfirmDialog(false);
    navigate(`/user-edit/${selectedUserId}`);
  };

  const [rotate, setRotate] = React.useState(0);
  const handleMouseEnter = () => {
    setRotate(360);
  };

  const handleMouseLeave = () => {
    setRotate(180);
  };

  if (meData && meData.role_id === 3) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        flexDirection="column"
      >
        <Typography variant="h4" color="error">
          403 - Unauthorized
        </Typography>
        <Typography>You are not authorized to access this page.</Typography>
      </Box>
    );
  }

  const getOriginalFileName = (url) => {
    const lastPart = url.split("/").pop(); // obito 6-2pG6kMtPPu874q2ib797PljnJaJUUI.jpg
    const ext = lastPart.substring(lastPart.lastIndexOf(".")); // .jpg
    const name = lastPart.split("-")[0]; // obito 6
    return `${name}${ext}`;
  };

  const downloadImage = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = getOriginalFileName(url);
    link.click();

    setRowSelection({
      open: true,
      severity: "success",
      message: "Image downloaded successfully",
    });
  };


  return (
    <DashboardLayout>
      {/* <Card id="delete-account">
        <MDBox mb={2} />
      </Card> */}
      <MDBox mt={3} />
      <Dialog
        open={openImage}
        onClose={() => setOpenImage(false)}
        maxWidth="md"
        PaperProps={{
          sx: {
            background: "transparent",
            boxShadow: "none",
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(0,0,0,0.4)",
          },
        }}
      >
        <Box position="relative">
          {/* Download Button */}
          <IconButton
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              background: "#fff",
              zIndex: 2,
              "&:hover": { background: "#eee" },
            }}
            onClick={() => downloadImage(selectedImage)}
          >
            <DownloadIcon />
          </IconButton>

          {/* Full Image */}
          <Box
            component="img"
            src={selectedImage}
            alt="Profile Full"
            sx={{
              maxHeight: "80vh",
              maxWidth: "100%",
              borderRadius: 2,
              cursor: "pointer",
            }}
            onClick={() => downloadImage(selectedImage)}
          />
        </Box>
      </Dialog>
      <Card id="delete-account" sx={{ mb: 2 }}>
        <MDBox pt={2} px={2} pb={3}>
          {/* <MDBox mt={5} mb={3}> */}
          <Grid container spacing={1} display={"flex"} alignItems={"center"}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <MDTypography variant="h5" fontWeight="medium">
                User List
              </MDTypography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }} display="flex" justifyContent="flex-end">
              <MDButton
                color={"dark"}
                onClick={() => {
                  navigate("/user/add-user");
                }}
                sx={{
                  color: "white !important",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#155fa0",
                  },
                  minWidth: "120px",
                }}
              >
                + Add User
              </MDButton>
            </Grid>
          </Grid>
          {/* </MDBox> */}
        </MDBox>
        <MDBox px={2} pt={0.5}>
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDAutocomplete
                label="User Name"
                name="txtUserName"
                value={filters.txtUserName}
                options={allUsersData}
                onChange={(newValue) => {
                  setFilters({ ...filters, txtUserName: newValue });
                }}
                isFilter={true}
                getOptionLabel={(option) => option.label}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDTextField
                name="txtEmail"
                value={filters.txtEmail}
                onChange={(e) => setFilters({ ...filters, txtEmail: e })}
                placeholder="Enter Email id"
                displayName="Email Id"
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDTextField
                name="intPhone"
                value={filters.intPhone}
                onChange={(e) => setFilters({ ...filters, intPhone: e })}
                placeholder="Enter Mobile Number"
                displayName="Mobile Number"
                inputType="numeric"
              />
            </Grid>
          </Grid>
        </MDBox>
        <MDBox px={2} pt={0.5}>
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDAutocomplete
                label="District"
                name="txtDistrict"
                value={filters.txtDistrict}
                options={districtData}
                onChange={(newValue) => {
                  setFilters({ ...filters, txtDistrict: newValue });
                }}
                isFilter={true} // optional: add a flag if you need special styling
              />
            </Grid>
            {/* <Grid item size={{ xs: 12, md: 4 }}>
              <MDAutocomplete
                label="Gender"
                name="txtGender"
                value={filters.txtGender}
                options={genders}
                onChange={(newValue) => {
                  setFilters({ ...filters, txtGender: newValue });
                }}
                isFilter={true} // optional: add a flag if you need special styling
              />
            </Grid> */}
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDAutocomplete
                label="Status"
                name="intStatus"
                value={filters.intStatus}
                options={statusData}
                onChange={(newValue) => {
                  setFilters({ ...filters, intStatus: newValue });
                }}
                isFilter={true} // optional: add a flag if you need special styling
                getOptionLabel={(option) => option.label}
              />
            </Grid>
          </Grid>
        </MDBox>
        <MDBox px={2} pt={0.5}>
          <Grid container spacing={3}>
            {/* <Grid item size={{ xs: 12, md: 4 }}>
              <MDTypography style={{ fontSize: "16px" }} color="dark">
                Date of Joining
              </MDTypography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={filters.txtDateofJoin}
                  format="DD/MM/YYYY"
                  onChange={(value) => setFilters({ ...filters, txtDateofJoin: value })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      InputProps: {
                        readOnly: true,
                        sx: {
                          fontSize: "14px",
                          height: "45px",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid> */}
          </Grid>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={2} justifyContent="center">
            {/* Search Button */}
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                onClick={() => fetchUsers(0, pagination.pageSize, filters, sorting)}
                sx={{
                  color: "white !important",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#155fa0",
                  },
                  minWidth: "120px",
                }}
              >
                Search
              </MDButton>
            </Grid>

            {/* Cancel Button */}
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                variant="outlined"
                onClick={() => {
                  const cleared = {
                    txtUserName: "",
                    txtEmail: "",
                    intPhone: "",
                    txtDistrict: "",
                    // txtGender: "",
                    intStatus: "",
                    // txtDateofJoin: null,
                  };
                  setFilters(cleared);
                  fetchUsers(0, pagination.pageSize, cleared);
                }}
                sx={{
                  color: "#b71c1c !important",
                  borderColor: "#b71c1c",
                  "&:hover": {
                    backgroundColor: "#fbe9e7",
                    borderColor: "#b71c1c",
                  },
                  minWidth: "120px",
                }}
              >
                Cancel
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </Card>
      <Box>
        <Grid container spacing={5}>
          <Grid item size={{ xs: 12, md: 12 }} className="listTable">
            {/* <Card sx={{ padding: 3, boxShadow: 3 }}> */}
            {/* {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : ( */}
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Paper
                elevation={2}
                sx={{
                  width: "100%",
                  overflowX: "auto",
                  backgroundColor: "#fff",
                  boxShadow: "none",
                  p: 2,
                }}
              >
                <MaterialReactTable
                  columns={columns}
                  data={data}
                  getRowId={(row) => row.user_id}
                  enableGlobalFilter={false}
                  enableFullScreenToggle={false}
                  enableDensityToggle={false}
                  initialState={{ showColumnFilters: false }}
                  layoutMode="grid"
                  enableRowNumbers
                  enableStickyHeader
                  manualFiltering
                  manualPagination
                  manualSorting
                  onPaginationChange={setPagination}
                  onSortingChange={setSorting}
                  rowCount={rowCount}
                  // onColumnFiltersChange={setColumnFilters}
                  onColumnFiltersChange={setColumnFilters}
                  // enableFilters
                  // enableColumnFilterModes
                  // muiLinearProgressProps={{
                  //   color: "inherit",
                  //   sx: { height: "4px" },
                  // }}
                  state={{
                    // rowSelection: selectedRowIds,

                    columnFilters,
                    // globalFilter,
                    isLoading: loading,
                    pagination,
                    // showAlertBanner: isError,
                    // showProgressBars: isRefetching,
                    sorting,
                  }}
                  // muiTableContainerProps={{
                  //   sx: {
                  //     maxHeight: "70vh",
                  //     overflowY: "auto",
                  //   },
                  // }}
                  muiTableHeadProps={{
                    sx: {
                      padding: "0px", // won't apply directly to <thead>, but useful for general structure
                      backgroundColor: "#011F3A", // background on <thead> element itself
                    },
                  }}
                  enableColumnActions={false}
                  muiTableHeadCellProps={{
                    sx: {
                      backgroundColor: "#004030",
                      color: "#fff",
                      // fontSize: '16px',
                      fontWeight: "bold",
                      position: "sticky", // Make header sticky
                      top: 0, // Stick it at the top
                      zIndex: 1, // Ensure it stays
                      "& .MuiTableSortLabel-root": {
                        "& .MuiTableSortLabel-icon": {
                          color: "#fff !important",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                        fontSize: "12px",
                        "::placeholder": {
                          color: "#e0e0e0", // Default (blurred) placeholder color
                          opacity: 1,
                        },
                      },
                      "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                        color: "#ffffff", // Focused placeholder color
                      },
                      "& .MuiIconButton-root": {
                        color: "#fff",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#e0e0e0",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff",
                      },

                      "& .MuiCheckbox-root": {
                        color: "#fff",
                      },
                    },
                  }}
                  muiTableBodyCellProps={{
                    sx: {
                      padding: "8px", // reduce from default 16px
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  muiTableBodyRowProps={({ row }) => ({
                    sx: {
                      backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0", // soft alternating colors
                    },
                  })}
                  renderTopToolbarCustomActions={({ table }) => (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "1rem",
                        p: "0.5rem",
                        flexWrap: "wrap",
                        marginLeft: "auto",
                      }}
                    >
                      <Tooltip title={"Export data to excel sheet"} arrow>
                        <Button
                          onClick={handleExportData}
                          sx={{
                            background:
                              "linear-gradient(90deg, rgba(2, 183, 255, 0.65) 30%,rgb(110, 199, 235) 100%)",
                            color: "#f4f4f4",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                            transition: "background 0.3s, transform 0.3s",
                            "&:hover": {
                              background:
                                "linear-gradient(90deg, rgba(55, 198, 254, 0.97) 30%,rgb(39, 169, 220) 100%))",
                              transform: "scale(1.05)",
                            },
                            "&:active": {
                              transform: "scale(0.95)",
                            },
                          }}
                          startIcon={<IconTableExport />}
                        >
                          {"Export To Excel"}
                        </Button>
                      </Tooltip>
                    </Box>
                  )}
                  muiTopToolbarProps={{
                    sx: {
                      backgroundColor: "#fff",
                      boxShadow: "none",
                    },
                  }}
                />
              </Paper>
            </Box>
            {/* )} */}
            {/* </Card> */}
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 2,
            boxShadow: 6,
            position: "relative",
            overflow: "visible",
          },
        }}
      >
        <IconButton
          onClick={() => setShowConfirmDialog(false)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            position: "absolute",
            top: -18,
            right: -16,
            backgroundColor: "#fff",
            color: "#ff0a54",
            boxShadow: 1,
            zIndex: 1500,
            p: 1.5,
            "&:hover": {
              backgroundColor: "#ff0a54",
              color: "#fff",
            },
            transition: "transform 0.5s ease",
            transform: `rotate(${rotate}deg)`,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{
            background: "linear-gradient(45deg, #f48c06, #191919)",
            color: "white !important",
            fontWeight: "bold",
            fontSize: "18px",
            textAlign: "center",
            position: "relative",
            zIndex: 1400,
            py: 2,
          }}
        >
          Confirm Edit
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ color: "#000", textAlign: "center" }}>
            Are you sure you want to edit this user?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleConfirmEdit}
            variant="contained"
            size="small"
            sx={{
              background: "linear-gradient(45deg, #2e7d32, #191919)",
              color: "#fff",
              fontWeight: "bold",
              transition: "transform 0.3s ease-in-out",
              "&:hover": {
                background: "linear-gradient(45deg, #1e5722, #101010)",
                color: "#fff",
                fontWeight: "bold",
                transform: "scale(1.05)",
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.4)",
              },
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message.status)}
        autoHideDuration={3000}
        onClose={() => setMessage({ status: "", title: "", msg: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setMessage({ status: "", title: "", msg: "" })}
          severity={message.status}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message.msg}
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
};

export default UserList;
